/* ===========================================================================
 * cro-explode-video.js  —  PROTOTYPE
 * ---------------------------------------------------------------------------
 * Frame-sequence "exploded view" scroll-scrub, paired with
 * sections/section-cro-explode-video.liquid.
 *
 * - Preloads the frame images (URLs emitted server-side as JSON by the
 *   section), draws the first frame ASAP, shows a lightweight loading state
 *   until a buffer is ready — never blocks the page.
 * - GSAP ScrollTrigger pins the stage and scrubs: progress -> nearest frame
 *   -> cover-draw to canvas (aspect-preserved, DPR-aware for crispness).
 * - gsap.matchMedia(): the scrub only builds under
 *   (prefers-reduced-motion: no-preference); the reduce branch draws a single
 *   static frame with no pin.
 * - Supports the theme editor (shopify:section:load / :unload) and guards
 *   against double-init.
 *
 * Loaded ONLY by the section (which only renders on the proto page).
 * ======================================================================== */
(function () {
  'use strict';

  var MAX_DPR = 2; // cap device-pixel-ratio so retina phones don't over-allocate

  // Add ?debug to the page URL to show a small overlay with the current frame.
  var DEBUG = false;
  try { DEBUG = new URLSearchParams(window.location.search).has('debug'); } catch (e) {}

  /* Contain-draw an image into the canvas: scale to FIT, center, letterbox.
   * The frame's own background is the stage background colour (#CCC8FB), so
   * the letterboxed area is visually indistinguishable from the frame — the
   * product is always fully visible, with no black bars or cropping. */
  function coverDraw(ctx, img, cssW, cssH, dpr) {
    if (!img || !img.naturalWidth) return;
    var iw = img.naturalWidth;
    var ih = img.naturalHeight;
    var scale = Math.min(cssW / iw, cssH / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    var dx = (cssW - dw) / 2;
    var dy = (cssH - dh) / 2;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in CSS pixels, render at DPR
    ctx.clearRect(0, 0, cssW, cssH);
    ctx.drawImage(img, dx, dy, dw, dh);
  }

  function initInstance(root) {
    if (!root || root.dataset.evInit === 'true') return;

    var canvas = root.querySelector('[data-cro-ev-canvas]');
    var stage = root.querySelector('[data-cro-ev-stage]');
    var loadingEl = root.querySelector('[data-cro-ev-loading]');
    var framesTag = root.querySelector('[data-cro-ev-frames]');
    if (!canvas || !stage || !framesTag) return;

    var frameUrls;
    try {
      frameUrls = JSON.parse((framesTag.textContent || '').trim());
    } catch (e) {
      console.warn('[cro-ev] could not parse frame list', e);
      return;
    }
    frameUrls = (frameUrls || []).filter(Boolean);
    var frameCount = frameUrls.length;
    if (!frameCount) return;

    root.dataset.evInit = 'true';

    var ctx = canvas.getContext('2d');
    var pinScroll = parseInt(root.dataset.pinScroll, 10) || 2400;
    var jumpST = null;     // ScrollTrigger ref, used by the header's frame-jump nav
    var stepTimes = {};    // frame(1-based) -> absolute timeline time of that step's hold
    var jumpTotal = 0;     // total timeline duration (for normalising step times)

    var images = new Array(frameCount);
    var loadedCount = 0;
    var currentIndex = -1;
    var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    var dims = { cssW: 0, cssH: 0 };

    var debugEl = null;
    if (DEBUG) {
      debugEl = document.createElement('div');
      debugEl.setAttribute('data-cro-ev-debug', '');
      debugEl.style.cssText = 'position:fixed;bottom:12px;right:12px;z-index:99999;' +
        'background:rgba(0,0,0,.82);color:#7CFC7C;font:12px/1.45 ui-monospace,Menlo,Consolas,monospace;' +
        'padding:7px 10px;border-radius:6px;pointer-events:none;white-space:pre;letter-spacing:.02em;';
      document.body.appendChild(debugEl);
    }
    function updateDebug() {
      if (!debugEl) return;
      var shown = currentIndex < 0 ? 0 : currentIndex;
      var pct = frameCount > 1 ? Math.round((shown / (frameCount - 1)) * 100) : 0;
      debugEl.textContent = 'frame ' + (shown + 1) + ' / ' + frameCount + '\nprogress ' + pct + '%';
    }
    updateDebug();

    function sizeCanvas() {
      var rect = stage.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      dims.cssW = rect.width;
      dims.cssH = rect.height;
      canvas.style.width = dims.cssW + 'px';
      canvas.style.height = dims.cssH + 'px';
      canvas.width = Math.round(dims.cssW * dpr);
      canvas.height = Math.round(dims.cssH * dpr);
    }

    function drawIndex(i, force) {
      var idx = i | 0;
      if (idx < 0) idx = 0;
      if (idx > frameCount - 1) idx = frameCount - 1;
      if (idx === currentIndex && !force) return;
      var img = images[idx];
      if (img && img.complete && img.naturalWidth) {
        coverDraw(ctx, img, dims.cssW, dims.cssH, dpr);
        currentIndex = idx;
        updateDebug();
        updateActiveNav();
      }
    }

    sizeCanvas();

    /* ---- Header nav active state (frame steps + Shop section) ------------ */
    var navFrameLinks = [];   // { frame, el } for data-cro-ev-jump links
    var navShopLinks = [];     // data-cro-ev-scroll links (Shop)
    var allNavLinks = [];
    var shopInView = false;
    var shopIO = null;
    (function collectNav() {
      var jumps = document.querySelectorAll('[data-cro-ev-jump]');
      for (var a = 0; a < jumps.length; a++) {
        var fr = parseInt(jumps[a].getAttribute('data-frame'), 10);
        navFrameLinks.push({ frame: isNaN(fr) ? 1 : fr, el: jumps[a] });
        allNavLinks.push(jumps[a]);
      }
      navFrameLinks.sort(function (x, y) { return x.frame - y.frame; });
      var scrolls = document.querySelectorAll('[data-cro-ev-scroll]');
      for (var b = 0; b < scrolls.length; b++) { navShopLinks.push(scrolls[b]); allNavLinks.push(scrolls[b]); }
    })();

    function setActiveNav(el) {
      for (var i = 0; i < allNavLinks.length; i++) {
        allNavLinks[i].classList.toggle('is-active', allNavLinks[i] === el);
      }
    }
    function updateActiveNav() {
      // Shop section in view wins; otherwise the last step we've reached.
      if (shopInView && navShopLinks.length) { setActiveNav(navShopLinks[0]); return; }
      var cf = (currentIndex < 0 ? 0 : currentIndex) + 1;
      var active = null;
      for (var i = 0; i < navFrameLinks.length; i++) {
        if (cf >= navFrameLinks[i].frame) active = navFrameLinks[i].el;
      }
      setActiveNav(active);
    }

    var shopTarget = document.querySelector('[data-cro-shop-target]');
    if (shopTarget && 'IntersectionObserver' in window) {
      shopIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) shopInView = entries[i].isIntersecting;
        updateActiveNav();
      }, { threshold: 0.35 });
      shopIO.observe(shopTarget);
    }

    /* ---- Preload (non-blocking) ------------------------------------------ */
    // Hide the loading state once ~25% of frames (or all, if fewer) are in.
    var bufferTarget = Math.max(1, Math.min(frameCount, Math.ceil(frameCount * 0.25)));
    var ready = false;

    function onFrameSettled(i) {
      loadedCount++;
      if (i === 0) drawIndex(0, true); // first frame the moment it arrives
      if (!ready && loadedCount >= bufferTarget) {
        ready = true;
        if (loadingEl) loadingEl.classList.add('is-hidden');
        root.dataset.evReady = 'true';
        // Re-measure once pinning/layout has settled.
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      }
    }

    frameUrls.forEach(function (url, i) {
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () { onFrameSettled(i); };
      img.onerror = function () { onFrameSettled(i); };
      img.src = url;
      images[i] = img;
    });

    /* ---- Animation ------------------------------------------------------- */
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    // Fallback: no GSAP -> just show the first frame statically.
    if (!gsap || !ScrollTrigger) {
      var poll = setInterval(function () {
        if (images[0] && images[0].complete && images[0].naturalWidth) {
          sizeCanvas();
          drawIndex(0, true);
          clearInterval(poll);
        }
      }, 60);
      root._croEvCleanup = function () {
        clearInterval(poll);
        root.dataset.evInit = '';
      };
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    var mm = gsap.matchMedia();

    // Full scroll-scrub experience: a pinned timeline that travels frames, then
    // HOLDS on each step's frame while that step's side message scrolls in from
    // the bottom, holds, and scrolls out the top (fading) — then continues.
    mm.add('(prefers-reduced-motion: no-preference)', function () {
      // Message steps from the DOM, sorted by frame.
      var stepEls = root.querySelectorAll('[data-step-frame]');
      var steps = [];
      for (var s = 0; s < stepEls.length; s++) {
        var fr = parseInt(stepEls[s].getAttribute('data-step-frame'), 10);
        if (isNaN(fr)) continue;
        if (fr < 1) fr = 1;
        if (fr > frameCount) fr = frameCount;
        steps.push({ frame: fr, idx: fr - 1, el: stepEls[s] });
      }
      steps.sort(function (a, b) { return a.idx - b.idx; });

      // Relative timing weights (scrub maps the whole timeline across pinScroll).
      var FRAME_UNIT = 1;                 // scroll weight per frame travelled
      var MSG_IN = 14, MSG_HOLD = 8, MSG_OUT = 14;
      var ENTER = 35, EXIT = -35;         // yPercent the message travels through

      var fp = { f: 0 };
      stepTimes = {};

      var tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: stage,
          start: 'top top',
          end: '+=' + pinScroll,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
          scrub: true,
          invalidateOnRefresh: true,
          onRefresh: function () {
            sizeCanvas();
            drawIndex(Math.round(fp.f), true);
          }
        },
        onUpdate: function () { drawIndex(Math.round(fp.f)); }
      });

      var prevIdx = 0;
      steps.forEach(function (step) {
        // Travel frames to this step.
        tl.to(fp, { f: step.idx, duration: Math.max(FRAME_UNIT, Math.abs(step.idx - prevIdx) * FRAME_UNIT) });
        // Message in (frame holds here — fp is not tweened during the message).
        tl.fromTo(step.el,
          { yPercent: ENTER, autoAlpha: 0 },
          { yPercent: 0, autoAlpha: 1, duration: MSG_IN, ease: 'power2.out' });
        stepTimes[step.frame] = tl.duration(); // message fully in -> good nav landing
        tl.to(step.el, { duration: MSG_HOLD });
        tl.to(step.el, { yPercent: EXIT, autoAlpha: 0, duration: MSG_OUT, ease: 'power2.in' });
        prevIdx = step.idx;
      });
      // Finish the remaining frames to the end.
      if (prevIdx < frameCount - 1) {
        tl.to(fp, { f: frameCount - 1, duration: Math.max(FRAME_UNIT, (frameCount - 1 - prevIdx) * FRAME_UNIT) });
      }

      // Intro slide (frame 1): drifts up + fades out, gone by its fade frame.
      // Placed at absolute position 0 so it overlaps the first frame travel.
      var introEl = root.querySelector('[data-cro-ev-intro]');
      if (introEl) {
        var introFade = parseInt(introEl.getAttribute('data-intro-fade-frame'), 10);
        if (isNaN(introFade) || introFade < 2) introFade = 8;
        tl.to(introEl, { yPercent: -12, autoAlpha: 0, ease: 'power1.in', duration: Math.max(1, introFade - 1) }, 0);
      }

      jumpST = tl.scrollTrigger;
      jumpTotal = tl.duration();
      sizeCanvas();
      drawIndex(0, true);

      return function () {
        if (tl.scrollTrigger) tl.scrollTrigger.kill();
        tl.kill();
        jumpST = null;
        if (stepEls.length) gsap.set(stepEls, { clearProps: 'all' });
        if (introEl) gsap.set(introEl, { clearProps: 'all' });
      };
    });

    // Reduced motion: one static frame (middle of the sequence), no pin.
    mm.add('(prefers-reduced-motion: reduce)', function () {
      var staticIdx = Math.floor(frameCount / 2);
      sizeCanvas();
      (function ensure() {
        var img = images[staticIdx];
        if (img && img.complete && img.naturalWidth) {
          drawIndex(staticIdx, true);
        } else {
          setTimeout(ensure, 80);
        }
      })();
      return function () {};
    });

    /* ---- Resize ---------------------------------------------------------- */
    var resizeT;
    function onResize() {
      clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        sizeCanvas();
        drawIndex(currentIndex < 0 ? 0 : currentIndex, true);
        ScrollTrigger.refresh();
      }, 150);
    }
    window.addEventListener('resize', onResize);

    /* ---- Frame-jump nav (header links: data-cro-ev-jump / data-frame) ---- */
    // Smooth-scroll the window to the scroll position that shows a given frame.
    function onJumpClick(e) {
      // Shop link -> smooth-scroll to the product range section.
      var scrollLink = e.target.closest ? e.target.closest('[data-cro-ev-scroll]') : null;
      if (scrollLink) {
        e.preventDefault();
        var shopT = document.querySelector('[data-cro-shop-target]');
        if (shopT) shopT.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      var link = e.target.closest ? e.target.closest('[data-cro-ev-jump]') : null;
      if (!link) return;
      e.preventDefault();
      if (!jumpST) return; // no pinned scrub (e.g. reduced motion) -> ignore
      var f = parseInt(link.getAttribute('data-frame'), 10);
      if (isNaN(f)) return;
      var t; // normalised position (0..1) along the pinned timeline
      if (stepTimes[f] != null && jumpTotal > 0) {
        t = stepTimes[f] / jumpTotal; // land where this step's message is fully in
      } else {
        if (f < 1) f = 1;
        if (f > frameCount) f = frameCount;
        t = frameCount > 1 ? (f - 1) / (frameCount - 1) : 0; // linear fallback
      }
      var target = jumpST.start + t * (jumpST.end - jumpST.start);
      window.scrollTo({ top: target, behavior: 'smooth' });
    }
    document.addEventListener('click', onJumpClick);

    /* ---- Editor cleanup -------------------------------------------------- */
    root._croEvCleanup = function () {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('click', onJumpClick);
      if (shopIO) shopIO.disconnect();
      if (debugEl && debugEl.parentNode) debugEl.parentNode.removeChild(debugEl);
      clearTimeout(resizeT);
      try { mm.revert(); } catch (e) {}
      root.dataset.evInit = '';
      root.dataset.evReady = '';
      currentIndex = -1;
    };
  }

  function initAll() {
    var nodes = document.querySelectorAll('[data-cro-ev]');
    for (var i = 0; i < nodes.length; i++) initInstance(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor: re-init / tear down as sections are added or removed. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector
      ? e.target.querySelector('[data-cro-ev]')
      : null;
    if (root) initInstance(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector
      ? e.target.querySelector('[data-cro-ev]')
      : null;
    if (root && typeof root._croEvCleanup === 'function') root._croEvCleanup();
  });
})();
